import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
	ServicesProvider,
	TitlingServiceConfigDtoInput,
} from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to create or update titling service configuration for a services provider
 */
export const createTitlingService = async (
	servicesProviderId: string,
	data: TitlingServiceConfigDtoInput,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.titling(servicesProviderId),
		data,
	);
	return response.data;
};

/**
 * Hook for creating or updating titling service configuration for a services provider
 */
export const useUpdateTitlingService = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: TitlingServiceConfigDtoInput) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to create titling service without a services provider ID.',
				);
			}
			return createTitlingService(servicesProviderId, data);
		},
		onSuccess: (data) => {
			// Optimistic cache update
			queryClient.setQueryData(
				queries.servicesProviders.detail(data.id).queryKey,
				data,
			);
		},
	});
};
